import React, { useState, useEffect, useCallback } from 'react';
import { useKanban } from './KanbanContext';
import { updateTaskInLambda } from '../utils/lambdaApi';
import { generateLocalTimestamp } from '../utils/taskUtils';
import { useAuth } from './AuthContext';
import useEscapeKey from '../utils/useEscapeKey';
import './MoveTaskModal.css';

function MoveTaskModal({ task, isOpen, onClose, onSuccess }) {
  const { getAllAvailableWorkspaces, activeWorkspace, workspaceData } = useKanban();
  const { currentUser } = useAuth();
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingWorkspaces, setIsLoadingWorkspaces] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredWorkspaces, setFilteredWorkspaces] = useState([]);

  // Cerrar modal con tecla Escape
  useEscapeKey(() => {
    if (isOpen && !isLoading) {
      onClose();
    }
  }, isOpen);

  const loadAvailableWorkspaces = useCallback(async () => {
    try {
      setIsLoadingWorkspaces(true);
      setError('');
      
      // Obtener todos los workspaces disponibles
      const workspaces = await getAllAvailableWorkspaces();
      
      // Filtrar el workspace actual para no mostrarlo como opción
      const currentWorkspaceId = task.workspace || activeWorkspace;
      const filteredWorkspaces = workspaces.filter(ws => ws.id !== currentWorkspaceId);
      
      setAvailableWorkspaces(filteredWorkspaces);
      setFilteredWorkspaces(filteredWorkspaces);
      
      // Seleccionar el primer workspace disponible por defecto
      if (filteredWorkspaces.length > 0) {
        setSelectedWorkspace(filteredWorkspaces[0].id);
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
      setError('Error al cargar los tableros disponibles');
    } finally {
      setIsLoadingWorkspaces(false);
    }
  }, [task, activeWorkspace, getAllAvailableWorkspaces]);

  // Cargar workspaces disponibles cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      loadAvailableWorkspaces();
    }
  }, [isOpen, loadAvailableWorkspaces]);

  // Filtrar workspaces según el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredWorkspaces(availableWorkspaces);
    } else {
      const filtered = availableWorkspaces.filter(workspace =>
        workspace.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredWorkspaces(filtered);
    }
  }, [searchTerm, availableWorkspaces]);

  const handleMoveTask = async () => {
    if (!selectedWorkspace) {
      setError('Por favor selecciona un tablero de destino');
      return;
    }

    if (!task.uuid && !task.id) {
      setError('Error: No se puede identificar la tarea');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const taskId = task.uuid || task.id;
      const currentWorkspaceId = task.workspace || activeWorkspace;
      
      // Obtener información del workspace de destino
      const targetWorkspace = availableWorkspaces.find(ws => ws.id === selectedWorkspace);
      const currentWorkspaceInfo = workspaceData.find(ws => ws.id === currentWorkspaceId);
      
      // Preparar las actualizaciones
      const updates = {
        workspace: selectedWorkspace
      };

      // Agregar entrada al historial de actividad
      const timestamp = generateLocalTimestamp();
      const userEmail = currentUser?.email || 'Usuario';
      
      const activityEntry = {
        timestamp,
        type: "WORKSPACE_CHANGE",
        field: "workspace",
        oldValue: currentWorkspaceInfo?.name || currentWorkspaceId,
        newValue: targetWorkspace?.name || selectedWorkspace,
        updatedBy: userEmail
      };

      // Incluir el historial de actividad actualizado
      const currentHistory = task.activityHistory || [];
      updates.activityHistory = [...currentHistory, activityEntry];

      // Actualizar la tarea en el backend
      await updateTaskInLambda(taskId, updates);

      // Llamar al callback de éxito
      if (onSuccess) {
        onSuccess(targetWorkspace?.name || selectedWorkspace);
      }

      // Cerrar el modal
      onClose();

    } catch (error) {
      console.error('Error moving task:', error);
      setError(`Error al mover la tarea: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  if (!isOpen) return null;

  return (
    <div className="move-task-modal-overlay" onClick={onClose}>
      <div className="move-task-modal" onClick={(e) => e.stopPropagation()}>
        <div className="move-task-modal-header">
          <div className="modal-title-section">
            <h3>
              <span className="move-icon">🔄</span>
              Mover Tarea
            </h3>
            <p className="task-title-preview">"{task.title}"</p>
          </div>
          <button 
            className="close-btn"
            onClick={onClose}
            disabled={isLoading}
          >
            ✕
          </button>
        </div>

        <div className="move-task-modal-content">
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          {isLoadingWorkspaces ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Cargando tableros disponibles...</p>
            </div>
          ) : (
            <>
              <div className="workspace-selection-section">
                <label className="section-label">
                  <span className="label-icon">📋</span>
                  Seleccionar tablero de destino:
                </label>

                {/* Buscador de tableros */}
                <div className="workspace-search-container">
                  <input
                    type="text"
                    placeholder="Buscar tablero..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="workspace-search-input"
                    disabled={isLoading}
                  />
                  {searchTerm && (
                    <button 
                      className="clear-search-btn"
                      onClick={clearSearch}
                      title="Limpiar búsqueda"
                      disabled={isLoading}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Lista de tableros */}
                <div className="workspaces-list">
                  {filteredWorkspaces.length > 0 ? (
                    filteredWorkspaces.map((workspace) => (
                      <div
                        key={workspace.id}
                        className={`workspace-option ${selectedWorkspace === workspace.id ? 'selected' : ''}`}
                        onClick={() => !isLoading && setSelectedWorkspace(workspace.id)}
                      >
                        <div className="workspace-option-content">
                          <span className="workspace-icon">{workspace.icon || '📁'}</span>
                          <span className="workspace-name">{workspace.name}</span>
                        </div>
                        {selectedWorkspace === workspace.id && (
                          <span className="selected-indicator">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-workspaces-message">
                      {searchTerm ? (
                        <p>No se encontraron tableros con el término "{searchTerm}"</p>
                      ) : (
                        <p>No hay tableros disponibles para mover la tarea</p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="move-info-section">
                <div className="info-box">
                  <span className="info-icon">ℹ️</span>
                  <div className="info-content">
                    <p>La tarea se moverá del tablero actual al tablero seleccionado.</p>
                    <p>Esta acción se registrará en el historial de la tarea.</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="move-task-modal-footer">
          <button 
            className="cancel-btn"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button 
            className="move-btn"
            onClick={handleMoveTask}
            disabled={isLoading || isLoadingWorkspaces || !selectedWorkspace}
          >
            {isLoading ? (
              <>
                <span className="loading-spinner small"></span>
                Moviendo...
              </>
            ) : (
              <>
                <span className="move-btn-icon">🔄</span>
                Mover Tarea
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MoveTaskModal; 